import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { CardStatementTotals } from "../../types/card-statement.types";
import { formatTransactionAmount } from "../../utils/cardTransactionDetailHelpers";

export interface StatementSummaryStripProps {
  totals: CardStatementTotals;
  /** True when the derived opening/closing balances could not be reconciled. */
  isEstimated: boolean;
}

export const StatementSummaryStrip = ({ totals, isEstimated }: StatementSummaryStripProps) => {
  const { t, activeLanguage } = useTranslations("cards");
  const money = (value: number) => formatTransactionAmount(value, totals.currency, activeLanguage);

  const primary = [
    {
      key: "opening",
      label: t("statement_summary_opening_balance"),
      value: money(totals.opening_balance),
      sub: isEstimated ? t("statement_summary_estimated_suffix") : null,
      estimated: true,
    },
    {
      key: "loaded",
      label: t("statement_summary_loaded"),
      value: `+${money(totals.loaded.amount)}`,
      sub: t("statement_summary_movements_count", { count: totals.loaded.count }),
      tone: "success" as const,
    },
    {
      key: "spent",
      label: t("statement_summary_spent"),
      value: `−${money(totals.spent.amount)}`,
      sub: t("statement_summary_movements_count", { count: totals.spent.count }),
      tone: "danger" as const,
    },
    {
      key: "closing",
      label: t("statement_summary_closing_balance"),
      value: money(totals.closing_balance),
      sub: isEstimated ? t("statement_summary_estimated_suffix") : null,
      estimated: true,
      highlight: true,
    },
  ];

  const secondary = [
    { key: "net", label: t("statement_summary_net"), value: money(totals.net) },
    { key: "available", label: t("statement_summary_available"), value: money(totals.available_balance) },
    { key: "declined", label: t("statement_summary_declined"), value: String(totals.declined_count) },
    { key: "pending", label: t("statement_summary_pending"), value: String(totals.pending_count) },
  ];

  return (
    <div className="px-10 pb-4 pt-5">
      <div className="grid grid-cols-4 divide-x divide-border overflow-hidden rounded-lg border border-border">
        {primary.map((cell) => (
          <div key={cell.key} className={cn("px-3 py-2.5", cell.highlight && "bg-primary/5")}>
            <p className="text-[8.5px] font-semibold uppercase tracking-wider text-muted-foreground">
              {cell.label}
              {cell.estimated && isEstimated && (
                <span aria-hidden="true" className="ml-0.5 align-super text-[7px]">
                  1
                </span>
              )}
            </p>
            <p
              className={cn(
                "mt-1 font-mono text-base font-semibold tabular-nums",
                cell.tone === "success" && "text-[var(--success)]",
                cell.tone === "danger" && "text-[var(--destructive)]",
                !cell.tone && cell.highlight && "text-primary",
                !cell.tone && !cell.highlight && "text-foreground",
                cell.estimated && isEstimated && "text-muted-foreground",
              )}
            >
              {cell.value}
            </p>
            {cell.sub && <p className="mt-0.5 text-[8.5px] text-muted-foreground">{cell.sub}</p>}
          </div>
        ))}
      </div>

      <div className="mt-2 grid grid-cols-4 divide-x divide-border rounded-lg border border-border bg-muted/40">
        {secondary.map((cell) => (
          <div key={cell.key} className="flex items-baseline justify-between gap-2 px-3 py-1.5">
            <span className="text-[8.5px] uppercase tracking-wider text-muted-foreground">{cell.label}</span>
            <span className="font-mono text-[10px] font-medium tabular-nums text-foreground">
              {cell.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
